import React, { useRef, useEffect } from 'react'

// JS-driven marquee: requestAnimationFrame loop, measures inner width and scrolls continuously
const TechMarquee = () => {
  const icons = [
    { className: 'fab fa-html5', title: 'HTML5' },
    { className: 'fab fa-css3-alt', title: 'CSS3' },
    { className: 'fab fa-js-square', title: 'JavaScript' },
    { className: 'fab fa-react', title: 'React' },
    { className: 'fab fa-angular', title: 'Angular' },
    { className: 'fab fa-vuejs', title: 'Vue.js' },
    { className: 'fab fa-wordpress', title: 'WordPress' },
    { className: 'fab fa-node-js', title: 'Node.js' },
    { className: 'fab fa-php', title: 'PHP' },
    { className: 'fab fa-python', title: 'Python' },
    { className: 'fas fa-robot', title: 'n8n' },
    { className: 'fab fa-github', title: 'GitHub' },
    { className: 'fab fa-docker', title: 'Docker' }
  ]

  // duplicate sequence for continuous loop (triplicated to cover wide viewports)
  const items = [...icons, ...icons, ...icons]

  const containerRef = useRef(null)
  const innerRef = useRef(null)
  const posRef = useRef(0)
  const loopWidthRef = useRef(0)
  const pausedRef = useRef(false)

  useEffect(() => {
    const inner = innerRef.current
    const container = containerRef.current
    if (!inner || !container) return

    const calc = () => {
      // Attempt to precisely measure the width of a single sequence by measuring
      // the distance between the first element of sequence A and the first element
      // of sequence B. This includes inter-item gaps.
      const children = inner.children
      let singleWidth = 0
      const seqLen = icons.length
      if (children && children.length >= seqLen * 2) {
        const startA = children[0].getBoundingClientRect().left
        const startB = children[seqLen].getBoundingClientRect().left
        singleWidth = startB - startA
      }

      // Fallback: use scrollWidth / number of repeats
      if (!singleWidth || !isFinite(singleWidth) || singleWidth <= 0) {
        const innerWidth = inner.scrollWidth || inner.getBoundingClientRect().width
        singleWidth = innerWidth / 2 || innerWidth
      }

      // Use a small buffer to avoid sub-pixel gaps on some browsers/devices
      const buffer = 2 // pixels of overlap to prevent visible seam
      loopWidthRef.current = Math.max(1, singleWidth + buffer)

      // normalize position into [-loopWidth, 0)
      if (loopWidthRef.current) {
        posRef.current = ((posRef.current % loopWidthRef.current) + loopWidthRef.current) % loopWidthRef.current
        if (posRef.current > 0) posRef.current -= loopWidthRef.current
      }
    }

    calc()

    // CSS animation approach for smoother GPU-accelerated animation.
    const speed = 45 // px per second (slower - adjust for desired speed)

    // Create a <style> element to hold dynamic keyframes for this instance
    const styleEl = document.createElement('style')
    let animName = `marquee_anim_${Date.now()}`
    styleEl.setAttribute('data-marquee', animName)
    document.head.appendChild(styleEl)

    const applyAnimation = () => {
      const loopWidth = loopWidthRef.current || 0
      if (!loopWidth) return
      const duration = Math.max(4, loopWidth / speed) // seconds, minimum to avoid too-fast loops

      // Add a tiny overlap in the translation to ensure there's no visible seam
      const overlap = 2

      // Define keyframes that translate the inner content by slightly more than one sequence width
      const keyframes = `@keyframes ${animName} { from { transform: translate3d(0,0,0); } to { transform: translate3d(-${loopWidth + overlap}px,0,0); } }`
      styleEl.textContent = keyframes

      // Apply animation to inner
      inner.style.animation = `${animName} ${duration}s linear infinite`
      inner.style.willChange = 'transform'
      inner.style.transform = 'translate3d(0,0,0)'
      inner.style.backfaceVisibility = 'hidden'
      inner.style.transformStyle = 'preserve-3d'
    }

    applyAnimation()

    const onResize = () => {
      // recalc loop width on resize and reapply animation with new duration/frames
      calc()
      applyAnimation()
    }
    window.addEventListener('resize', onResize)

    // pause on hover / focus for accessibility
    const onEnter = () => {
      pausedRef.current = true
      inner.style.animationPlayState = 'paused'
    }
    const onLeave = () => {
      pausedRef.current = false
      inner.style.animationPlayState = 'running'
    }
    const containerEl = containerRef.current
    containerEl?.addEventListener('mouseenter', onEnter)
    containerEl?.addEventListener('mouseleave', onLeave)
    containerEl?.addEventListener('focusin', onEnter)
    containerEl?.addEventListener('focusout', onLeave)

    return () => {
      window.removeEventListener('resize', onResize)
      containerEl?.removeEventListener('mouseenter', onEnter)
      containerEl?.removeEventListener('mouseleave', onLeave)
      containerEl?.removeEventListener('focusin', onEnter)
      containerEl?.removeEventListener('focusout', onLeave)
      // cleanup style element
      styleEl?.parentNode?.removeChild(styleEl)
      // clear animation
      if (inner) {
        inner.style.animation = ''
        inner.style.animationPlayState = ''
        inner.style.willChange = ''
      }
    }
  }, [])

  return (
    <div ref={containerRef} className="brand-marquee" aria-hidden="true" tabIndex={-1}>
      <div ref={innerRef} className="brand-marquee-inner">
        {items.map((ic, idx) => (
          <i key={idx} className={`${ic.className} marquee-icon`} title={ic.title} aria-hidden="true" />
        ))}
      </div>
    </div>
  )
}

export default TechMarquee
